/**
 * Fahhar.com — bounded infinite scroll
 *
 * Keeps the convenience of automatic loading without allowing a collection
 * page to grow forever. A browser view contains at most three Shopify pages;
 * after that, the shopper continues with a normal page navigation so the old
 * DOM, decoded images and card listeners can be released.
 */
(function () {
  'use strict';

  class FahharInfiniteScroll {
    constructor() {
      this.container = document.querySelector('[data-infinite-scroll-container]');
      if (!this.container) return;

      this.grid = this.container.querySelector('[data-infinite-grid]');
      this.skeleton = document.getElementById('InfiniteSkeleton');
      this.sentinel = this.container.querySelector('[data-sentinel]');

      if (!this.grid || !this.sentinel) return;

      this.currentPage = parseInt(this.container.dataset.currentPage, 10) || 1;
      this.totalPages = parseInt(this.container.dataset.totalPages, 10) || 1;

      // Initial Shopify page + two appended pages. With 16 products/page this
      // caps one document at 48 cards instead of hundreds of cards.
      this.maxPagesPerView = 3;
      this.pagesInView = 1;
      this.isLoading = false;
      this.renderedProductIds = new Set();
      this.observer = null;
      this.btnWrap = null;

      this.init();
    }

    init() {
      this.registerExistingProducts();
      this.purgeNativeThemeButtons();
      this.container.dataset.infiniteReady = 'true';

      if (this.currentPage >= this.totalPages) {
        this.finish();
        return;
      }

      this.setupContinueLink();
      this.setupObserver();
      this.bindFilterEvents();
    }

    registerExistingProducts() {
      this.grid.querySelectorAll('[data-product-id]').forEach((item) => {
        if (item.dataset.productId) {
          this.renderedProductIds.add(item.dataset.productId);
        }
      });
    }

    purgeNativeThemeButtons() {
      this.container
        .querySelectorAll('.collection__infinite-trigger, [data-click-to-load], .pagination')
        .forEach((element) => element.remove());
    }

    setupContinueLink() {
      this.btnWrap = document.createElement('div');
      this.btnWrap.className = 'infinite-load-more-wrap';
      this.btnWrap.hidden = true;
      this.container.appendChild(this.btnWrap);
    }

    showContinueLink() {
      if (!this.btnWrap || this.currentPage >= this.totalPages) return;

      const nextUrl = this.buildPageUrl(this.currentPage + 1);
      const link = document.createElement('a');
      link.className = 'btn btn--secondary';
      link.href = nextUrl;
      link.textContent = 'Sonraki Ürünlere Geç';

      this.btnWrap.replaceChildren(link);
      this.btnWrap.hidden = false;
    }

    setupObserver() {
      this.observer = new IntersectionObserver(
        (entries) => {
          const isVisible = entries.some((entry) => entry.isIntersecting);
          if (!isVisible || this.isLoading) return;

          if (this.currentPage >= this.totalPages) {
            this.finish();
            return;
          }

          if (this.pagesInView >= this.maxPagesPerView) {
            this.pauseForNavigation();
            return;
          }

          this.fetchNextPage();
        },
        {
          root: null,
          // A smaller margin avoids starting the next page too early on mobile.
          rootMargin: '100px 0px',
          threshold: 0
        }
      );

      this.observer.observe(this.sentinel);
    }

    async fetchNextPage() {
      if (this.isLoading || this.currentPage >= this.totalPages) return;

      this.isLoading = true;
      this.showSkeleton(true);

      const nextPage = this.currentPage + 1;
      const targetUrl = this.buildPageUrl(nextPage);

      try {
        const response = await fetch(targetUrl, {
          credentials: 'same-origin',
          headers: { 'X-Requested-With': 'XMLHttpRequest' }
        });

        if (!response.ok) {
          throw new Error(`Collection request failed with ${response.status}`);
        }

        const htmlText = await response.text();
        const doc = new DOMParser().parseFromString(htmlText, 'text/html');
        const newItems = doc.querySelectorAll('#InfiniteProductGrid [data-product-id]');

        if (!newItems.length) {
          this.currentPage = this.totalPages;
          this.finish();
          return;
        }

        const fragment = document.createDocumentFragment();
        let addedCount = 0;

        newItems.forEach((node) => {
          const productId = node.dataset.productId;
          if (!productId || this.renderedProductIds.has(productId)) return;

          this.renderedProductIds.add(productId);
          addedCount += 1;

          node.querySelectorAll('img').forEach((img) => {
            img.loading = 'lazy';
            img.decoding = 'async';
          });

          // The parsed document is detached. Appending its node directly lets
          // the browser adopt it and avoids a second deep clone in memory.
          fragment.appendChild(node);
        });

        if (!addedCount) {
          this.currentPage = this.totalPages;
          this.finish();
          return;
        }

        this.grid.appendChild(fragment);
        this.currentPage = nextPage;
        this.pagesInView += 1;
        this.container.dataset.currentPage = String(this.currentPage);

        if (this.currentPage >= this.totalPages) {
          this.finish();
        } else if (this.pagesInView >= this.maxPagesPerView) {
          this.pauseForNavigation();
        }
      } catch (error) {
        console.error('Infinite Scroll Error:', error);
        // A real link is a reliable fallback when fetch or parsing fails.
        this.showContinueLink();
        this.disconnectObserver();
      } finally {
        this.isLoading = false;
        this.showSkeleton(false);
      }
    }

    pauseForNavigation() {
      this.disconnectObserver();
      this.showContinueLink();
    }

    finish() {
      this.disconnectObserver();
      this.showSkeleton(false);

      if (this.sentinel) {
        this.sentinel.remove();
        this.sentinel = null;
      }

      if (this.btnWrap) {
        this.btnWrap.remove();
        this.btnWrap = null;
      }
    }

    disconnectObserver() {
      if (this.observer) {
        this.observer.disconnect();
        this.observer = null;
      }
    }

    buildPageUrl(page) {
      const url = new URL(window.location.href);
      url.searchParams.set('page', page);
      return url.toString();
    }

    showSkeleton(show) {
      if (!this.skeleton) return;
      this.skeleton.hidden = !show;
      this.skeleton.setAttribute('aria-hidden', String(!show));
    }

    bindFilterEvents() {
      // The theme owns the filtered grid replacement. Clear our saved state;
      // the replacement section can initialise a fresh instance.
      document.addEventListener('shopify:facet-select', () => {
        this.disconnectObserver();
        sessionStorage.removeItem('fahhar_scroll_state');
      });
    }
  }

  function start() {
    new FahharInfiniteScroll();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start, { once: true });
  } else {
    start();
  }
})();
